import React, { useState, useCallback } from "react";
import {
  Box, TextField, InputAdornment, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Paper, Chip, Avatar
} from "@mui/material";
import { Search, Block } from "@mui/icons-material";
import { db } from "../../../context/Firebase";
import { doc, getDoc, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { getPostContent, getPostImages } from "../../../shared/utils/postHelpers";
import { useNavigate } from "react-router-dom";

const BLOCKED_MESSAGE = "This hashtag is not available due to community guidelines.";

const HashtagSearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | blocked | found | empty
  // eslint-disable-next-line no-unused-vars
  const [latestRequestId, setLatestRequestId] = useState(0);
  const navigate = useNavigate();

  const handleSearch = useCallback(async (value, requestId) => {
    const tag = value.replace(/^#/, "").trim().toLowerCase();
    if (!tag) {
      setResults([]);
      setStatus("idle");
      return;
    }

    setStatus("loading");

    try {
      // Check if hashtag is blocked or reported
      const hashtagRef = doc(db, "hashtags", tag);
      const hashtagSnap = await getDoc(hashtagRef);

      if (hashtagSnap.exists()) {
        const hashtagData = hashtagSnap.data();
        if (hashtagData.isBlocked || hashtagData.isReported) {
          setLatestRequestId(prev => {
            if (requestId >= prev) {
              setResults([]);
              setStatus("blocked");
              return requestId;
            }
            return prev;
          });
          return;
        }
      }

      // Fetch posts with this hashtag
      const postsQuery = query(
        collection(db, "posts"),
        where("hashtags", "array-contains", tag),
        orderBy("createdAt", "desc")
      );
      const postsSnap = await getDocs(postsQuery);
      const posts = postsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      setLatestRequestId(prev => {
        if (requestId >= prev) {
          setResults(posts);
          setStatus(posts.length === 0 ? "empty" : "found");
          return requestId;
        }
        return prev;
      });
    } catch (err) {
      console.error("Hashtag search error:", err);
      setLatestRequestId(prev => {
        if (requestId >= prev) {
          setStatus("empty");
          return requestId;
        }
        return prev;
      });
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    const requestId = Date.now();
    setLatestRequestId(requestId);
    handleSearch(val, requestId);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 680, mx: "auto", mt: 2 }}>
      <TextField
        fullWidth
        placeholder="Search hashtags (e.g. #nature)"
        value={searchTerm}
        onChange={handleChange}
        variant="outlined"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: "#65676b" }} />
            </InputAdornment>
          ),
        }}
        sx={{ bgcolor: "white", borderRadius: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      />

      {/* Loading */}
      {status === "loading" && (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Blocked hashtag */}
      {status === "blocked" && (
        <Paper elevation={0} sx={{ mt: 2, p: 3, borderRadius: 2, border: "1px solid #ffd6d6", bgcolor: "#fff5f5", display: "flex", alignItems: "center", gap: 2 }}>
          <Block sx={{ color: "#d32f2f", fontSize: 32 }} />
          <Box>
            <Typography fontWeight={600} color="error">Hashtag Unavailable</Typography>
            <Typography variant="body2" color="text.secondary">{BLOCKED_MESSAGE}</Typography>
          </Box>
        </Paper>
      )}

      {/* No results */}
      {status === "empty" && (
        <Paper elevation={0} sx={{ mt: 2, p: 3, borderRadius: 2, border: "1px solid #e4e6eb", textAlign: "center" }}>
          <Typography color="text.secondary">No posts found for <strong>{searchTerm}</strong></Typography>
        </Paper>
      )}

      {/* Results */}
      {status === "found" && (
        <Paper elevation={1} sx={{ mt: 2, borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label={`#${searchTerm.replace(/^#/, "")}`} size="small" sx={{ bgcolor: "var(--app-accent)", color: "white", fontWeight: 600 }} />
            <Typography variant="body2" color="text.secondary">{results.length} post{results.length !== 1 ? "s" : ""}</Typography>
          </Box>
          <List disablePadding>
            {results.map((post, i) => {
              const content = getPostContent(post);
              const images = getPostImages(post);
              return (
                <React.Fragment key={post.id}>
                  {i > 0 && <Divider />}
                  <ListItem
                    button
                    alignItems="flex-start"
                    onClick={() => navigate(`/post/${post.id}`)}
                    sx={{ py: 1.5, "&:hover": { bgcolor: "#f5f5f5" } }}
                  >
                    <Avatar src={post.userProfilePic} sx={{ mr: 2, mt: 0.5, width: 36, height: 36, bgcolor: "var(--app-accent)" }}>
                      {post.userName?.[0]?.toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={<Typography fontWeight={600} variant="body2">{post.userName || "Unknown"}</Typography>}
                      secondary={
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 480 }}>
                          {content}
                        </Typography>
                      }
                    />
                    {images.length > 0 && (
                      <Box component="img" src={images[0]} alt="" sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 1, ml: 1, flexShrink: 0 }} />
                    )}
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default HashtagSearch;
